import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from 'react-i18next';
import { Link } from "react-router-dom";
import { UserContext } from "../../providers/UserProvider";

export type EeljProps = {
  id: string;
  active: boolean;
  name: string;
  title: string;
  description: string;
  thumb: string;
  ageGroup: string;
  price: number;
  start_date: Date;
  end_date: Date;
  orderStartDate: Date;
  orderEndDate: Date;
  orderLimit: number;
  orderCount: number;
  ITtraining: boolean;
  ITtrainingPrice: number;
  tulburdaraaavah?: boolean;

}

export const EeljInfo = (props: EeljProps) => {
  const { t } = useTranslation();
  const {userBio} = useContext(UserContext);
  const {id, name, ITtraining, ITtrainingPrice, active, title, description, thumb, price, ageGroup, start_date, end_date, orderStartDate, orderEndDate, orderLimit, orderCount} = props;
  const [curDate, setCurDate] = useState(new Date());


  const getDateString = (date: string | Date) => {
    return new Date(date).toISOString().split('T')[0].replaceAll('-', '.');
  }

  const countdownString = useMemo(() => {
    const diff = new Date(orderStartDate).valueOf() - curDate.valueOf();

    const formatDuration = (millis: number) => {
      let date = new Date(millis);
      let parts = [];

      if (date.getUTCFullYear() > 1970) {
          let years = date.getUTCFullYear() - 1970;
          parts.push(years, 'жил');
      }
      if (date.getUTCMonth() > 0) { // months start at zero
          let months = date.getUTCMonth();
          parts.push(months, 'сар');
      }
      if (date.getUTCDate() > 1) {
          let days = date.getUTCDate() - 1;
          parts.push(days, 'өдөр');
      }

      parts.push(date.toISOString().substring(11, 19));
      return parts.join(' ');
    }

    return formatDuration(diff);
  }, [curDate, orderStartDate]);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurDate(new Date());
    }, 1000);

    return () => {
      clearInterval(interval);
    }
  }, []);

  const orderStatus = curDate < new Date(orderEndDate)
    ? curDate > new Date(orderStartDate)
      ? orderCount < orderLimit
        ? 'active'
        : 'full'
      : 'pending'
    : 'ended';

  const orderProgress = orderLimit > 0 ? Math.min((orderCount / orderLimit) * 100, 100) : 0;

  return (
    <Link to={`/eelj/${id}`} className="group block">
      {/* Airbnb-style Card */}
      <div className="relative hover:shadow-lg transition-shadow duration-300 rounded-xl">
        {/* Image */}
        <div className="relative aspect-[20/19] overflow-hidden rounded-xl bg-neutral-100 mb-3">
          <img
            src={thumb}
            alt={title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300 ease-out"
          />

          {/* Gradient overlay on hover */}
          <div className="absolute inset-0 bg-gradient-to-t from-secondary-600/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

          {/* Active badge - Airbnb "Guest favourite" style */}
          {active && (
            <div className="absolute top-3 left-3">
              <span className="inline-flex items-center px-3 py-1.5 rounded-full bg-white text-neutral-900 text-xs font-bold shadow-md">
                {t('eelj.active')}
              </span>
            </div>
          )}

          {/* Status badge top-right */}
          {orderStatus === 'active' && (
            <div className="absolute top-3 right-3">
              <span className="inline-flex items-center px-3 py-1.5 rounded-full bg-gradient-to-r from-secondary-500 to-primary-600 text-white text-xs font-bold shadow-md">
                {t('eelj.orderOpen')}
              </span>
            </div>
          )}
        </div>

        {/* Info */}
        <div className="space-y-1">
          <div className="flex items-start justify-between gap-2">
            <h3 className="font-semibold text-[15px] text-neutral-900 leading-snug line-clamp-1 group-hover:text-primary-600 transition-colors">
              {title}
            </h3>
            {/* Rating/star style */}
            <div className="flex items-center gap-1 flex-shrink-0">
              <svg className="w-3.5 h-3.5 text-neutral-900" fill="currentColor" viewBox="0 0 20 20">
                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
              </svg>
            </div>
          </div>

          <p className="text-sm text-neutral-500">{name}</p>
          <p className="text-sm text-neutral-500">
            {getDateString(start_date)} – {getDateString(end_date)} · {ageGroup}
          </p>

          {/* Price - Airbnb style */}
          <div className="pt-1">
            <span className="font-bold text-[15px] bg-gradient-to-r from-primary-600 via-secondary-500 to-primary-400 bg-clip-text text-transparent">
              ₮{(price + ITtrainingPrice).toLocaleString()}
            </span>
            <span className="text-sm text-neutral-500"> {t('eelj.perChild')}</span>
          </div>

          {/* Order status - subtle */}
          {orderStatus === 'pending' && (
            <p className="text-xs text-primary-600 font-medium pt-1">
              {t('eelj.orderStartsIn')} {countdownString} {t('eelj.remaining')}
            </p>
          )}
          {orderStatus === 'full' && (
            <p className="text-xs text-neutral-400 font-medium pt-1">
              {t('eelj.orderFull')}
            </p>
          )}
          {orderStatus === 'ended' && (
            <p className="text-xs text-neutral-400 font-medium pt-1">
              {t('eelj.orderEnded')}
            </p>
          )}
        </div>
      </div>
    </Link>
  );
}

export default EeljInfo;
